// Cases panel — accusation browsing with clickable case list.
// No raw IDs visible to the user. Click a case to interact.

import type { Net } from "../net.js";
import type { ClientStore } from "../state.js";
import { openPanel, ensurePanelStyles, escape } from "./shared_panel.js";

type Filter = "active" | "mine" | "against_me" | "all";
let currentFilter: Filter = "active";
let selectedCase: number | null = null;

export function openCasesPanel(net: Net, store: ClientStore): void {
  ensurePanelStyles();
  net.send({ t: "cases", filter: currentFilter });

  openPanel({
    id: "cases-overlay",
    title: "Accusations — The Dockets",
    store,
    render: (s) => {
      const cases = s.cases_list ?? [];
      return `
        <div class="panel-tabs">
          ${(["active","mine","against_me","all"] as const).map((f) => `
            <button class="panel-tab ${f === currentFilter ? "active" : ""}" data-filter="${f}">${f.replace(/_/g, " ")}</button>
          `).join("")}
        </div>

        <div class="panel-section-title">Active Cases</div>
        ${cases.length === 0 ? `<div class="panel-hint">No cases match this filter.</div>` : `
          <div class="case-list">
            ${cases.map((c) => `
              <div class="case-row ${selectedCase === c.id ? "selected" : ""}" data-case-id="${c.id}">
                <div class="case-row-header">
                  <span class="case-parties">${escape(c.accuser)} v. <strong>${escape(c.accused)}</strong></span>
                  <span class="case-status status-${c.status}">${escape(c.status)}</span>
                </div>
                <div class="case-row-detail">
                  <span class="case-type">${escape(c.type)}</span>
                  <span class="case-jurisdiction">${escape(c.jurisdiction)}</span>
                  <span class="case-evidence">${c.current_evidence}/${c.required_evidence} evidence</span>
                  ${c.verdict && c.verdict !== "none" ? `<span class="case-verdict">${escape(c.verdict)}</span>` : ""}
                </div>
                <div class="case-actions">
                  <button class="case-act-btn" data-action="view" data-cid="${c.id}">View</button>
                  <button class="case-act-btn" data-action="testify" data-cid="${c.id}">Testify</button>
                  <button class="case-act-btn" data-action="evidence" data-cid="${c.id}">Evidence</button>
                  <button class="case-act-btn" data-action="appeal" data-cid="${c.id}">Appeal</button>
                </div>
              </div>
            `).join("")}
          </div>
        `}

        <hr style="border:0;border-top:1px solid var(--line);margin:16px 0;">

        <div class="panel-section-title">File a new accusation</div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:6px;margin-bottom:12px;">
          <input id="acc-target" type="text" placeholder="target character name" list="room-chars" />
          <datalist id="room-chars">
            ${(s.room?.others ?? []).map((o) => `<option value="${escape(o)}">`).join("")}
          </datalist>
          <select id="acc-type">
            <option value="witchcraft">Witchcraft</option>
            <option value="heresy">Heresy</option>
            <option value="corruption">Corruption</option>
            <option value="blasphemy">Blasphemy</option>
            <option value="possession">Possession</option>
          </select>
          <select id="acc-jurisdiction">
            <option value="hammer">Hammer</option>
            <option value="maleficium">Maleficium</option>
          </select>
          <button id="acc-file-btn">File</button>
        </div>
        <input id="acc-text" type="text" placeholder="Accusation statement (optional)" style="margin-bottom:12px;" />

        <div id="form-testify" style="display:none;border:1px solid var(--line);padding:8px;border-radius:4px;margin-bottom:8px;">
          <div class="panel-section-title">Testify on case: <strong id="form-testify-name"></strong></div>
          <select id="testify-type" style="margin-bottom:6px;">
            <option value="eyewitness">Eyewitness</option>
            <option value="expert">Expert</option>
            <option value="character">Character reference</option>
          </select>
          <textarea id="testify-text" placeholder="Your statement..." rows="3"></textarea>
          <div style="margin-top:6px;"><button id="testify-submit-btn">Submit testimony</button></div>
        </div>

        <div id="form-evidence" style="display:none;border:1px solid var(--line);padding:8px;border-radius:4px;">
          <div class="panel-section-title">Submit evidence on case: <strong id="form-evidence-name"></strong></div>
          <select id="evidence-type" style="margin-bottom:6px;">
            <option value="physical">Physical</option>
            <option value="supernatural">Supernatural</option>
            <option value="documentary">Documentary</option>
            <option value="testimonial">Testimonial</option>
            <option value="circumstantial">Circumstantial</option>
          </select>
          <input id="evidence-name" type="text" placeholder="Evidence name (short)" style="margin-bottom:6px;" />
          <textarea id="evidence-desc" placeholder="Description..." rows="3"></textarea>
          <div style="margin-top:6px;"><button id="evidence-submit-btn">Submit evidence</button></div>
        </div>
      `;
    },
    bind: (root, _s, close, refresh) => {
      // Tab clicks
      for (const btn of root.querySelectorAll<HTMLButtonElement>(".panel-tab")) {
        btn.addEventListener("click", () => {
          currentFilter = btn.dataset.filter as Filter;
          net.send({ t: "cases", filter: currentFilter });
          refresh();
        });
      }
      // Case action buttons (View, Testify, Evidence, Appeal)
      for (const btn of root.querySelectorAll<HTMLButtonElement>(".case-act-btn")) {
        btn.addEventListener("click", () => {
          const cid = Number(btn.dataset.cid);
          const action = btn.dataset.action;
          selectedCase = cid;
          const c = (_s.cases_list ?? []).find((x) => x.id === cid);
          const caseName = c ? `${c.accuser} v. ${c.accused}` : `#${cid}`;
          switch (action) {
            case "view":
              net.send({ t: "case_detail", accusation_id: cid });
              break;
            case "appeal":
              net.send({ t: "appeal", accusation_id: cid });
              break;
            case "testify": {
              const form = root.querySelector<HTMLElement>("#form-testify")!;
              form.style.display = "";
              root.querySelector<HTMLElement>("#form-testify-name")!.textContent = caseName;
              break;
            }
            case "evidence": {
              const form = root.querySelector<HTMLElement>("#form-evidence")!;
              form.style.display = "";
              root.querySelector<HTMLElement>("#form-evidence-name")!.textContent = caseName;
              break;
            }
          }
        });
      }
      // File accusation
      root.querySelector<HTMLButtonElement>("#acc-file-btn")?.addEventListener("click", () => {
        const target = root.querySelector<HTMLInputElement>("#acc-target")!.value.trim();
        const type = root.querySelector<HTMLSelectElement>("#acc-type")!.value as "witchcraft" | "heresy" | "corruption" | "blasphemy" | "possession";
        const jurisdiction = root.querySelector<HTMLSelectElement>("#acc-jurisdiction")!.value as "hammer" | "maleficium";
        const text = root.querySelector<HTMLInputElement>("#acc-text")!.value;
        if (!target) return;
        net.send({ t: "accuse", target, accusation_type: type, jurisdiction, text });
      });
      // Submit testimony
      root.querySelector<HTMLButtonElement>("#testify-submit-btn")?.addEventListener("click", () => {
        if (selectedCase === null) return;
        const type = root.querySelector<HTMLSelectElement>("#testify-type")!.value as "character" | "expert" | "eyewitness";
        const text = root.querySelector<HTMLTextAreaElement>("#testify-text")!.value.trim();
        if (!text) return;
        net.send({ t: "testify", accusation_id: selectedCase, testimony_type: type, text });
      });
      // Submit evidence
      root.querySelector<HTMLButtonElement>("#evidence-submit-btn")?.addEventListener("click", () => {
        if (selectedCase === null) return;
        const type = root.querySelector<HTMLSelectElement>("#evidence-type")!.value as "physical" | "testimonial" | "supernatural" | "circumstantial" | "documentary";
        const name = root.querySelector<HTMLInputElement>("#evidence-name")!.value.trim();
        const desc = root.querySelector<HTMLTextAreaElement>("#evidence-desc")!.value.trim();
        if (!name) return;
        net.send({ t: "evidence", accusation_id: selectedCase, evidence_type: type, name, description: desc });
      });
      void close;
    },
  });
}
